import { useEffect, useState } from 'react';
import api from '../lib/api';
import { getOrg } from '../lib/org';
import { useAuth } from '../lib/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { RefreshCw, Printer, ArrowRight, Inbox, Trash2, PhoneCall, AlertCircle, Copy, History } from 'lucide-react';
import HelpTip from '../components/HelpTip';
import EarlierInquiriesSheet from '../components/EarlierInquiriesSheet';
import { fmtDate } from '../lib/date';
import { buildTelUri, formatPhoneDisplay } from '../lib/phone';

const DISP_LABEL = {
  new: 'New',
  contacted: 'Contacted',
  no_response: 'No Response',
  duplicate: 'Duplicate',
  converted: 'Converted',
  not_eligible: 'Not Eligible',
  not_interested: 'Not Interested',
};
const DISP_TONE = {
  new: 'bg-[#B08A3A]/20 text-[#B08A3A] border-[#B08A3A]/40',
  contacted: 'bg-[#839E97]/15 text-[#839E97] border-[#839E97]/30',
  no_response: 'bg-[#BAA9CA]/15 text-[#685B72] border-[#BAA9CA]/30',
  duplicate: 'bg-[#E8E5EC] text-[#685B72] border-[#E8E5EC]',
  converted: 'bg-[#839E97]/25 text-[#839E97] border-[#839E97]/40 font-semibold',
  not_eligible: 'bg-[#8A3A3A]/20 text-[#8A3A3A] border-[#8A3A3A]/40',
  not_interested: 'bg-[#C9BFBF]/25 text-[#8A3A3A] border-[#C9BFBF]/45',
};

// Business days elapsed since an ISO date. Weekends are skipped so a lead
// contacted Friday afternoon isn't flagged as stale on Saturday.
function bdaysSince(iso) {
  if (!iso) return 0;
  const then = new Date(iso);
  if (isNaN(then)) return 0;
  const now = new Date();
  let d = 0;
  const cursor = new Date(then);
  while (cursor < now) {
    cursor.setDate(cursor.getDate() + 1);
    const dow = cursor.getDay();
    if (dow !== 0 && dow !== 6) d += 1;
  }
  return d;
}

// Given an inquiry row, return {stale: bool, bd: number, anchor: iso}
// indicating whether a contacted lead is overdue for a follow-up
// (>= 1 business day since last touch). New/other-status rows return
// stale=false.
function computeStale(inq) {
  if (inq.disposition !== 'contacted') return { stale: false, bd: 0, anchor: null };
  const attempts = inq.contact_attempts || [];
  let anchor = null;
  if (attempts.length) {
    anchor = attempts
      .map(a => a.at)
      .filter(Boolean)
      .sort()
      .slice(-1)[0];
  }
  anchor = anchor || inq.updated_at || inq.received_iso || inq.received_at;
  const bd = bdaysSince(anchor);
  return { stale: bd >= 1, bd, anchor };
}

// Fax-referral source list is fixed to 5 options (iter 38).
const FAX_REFERRAL_SOURCES = ['Pediatrician', 'ABA Center', 'Daycare', 'Hospital', 'Others'];
// Roles that can soft-delete an inquiry.
const DELETE_ROLES = new Set(['super_user', 'admin']);
const MAX_CONTACT_ATTEMPTS = 3;

export default function Inquiries() {
  const { user } = useAuth();
  const canDelete = DELETE_ROLES.has(user?.role);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('followup');
  const [source, setSource] = useState('all');
  const [syncing, setSyncing] = useState(false);
  const [openFax, setOpenFax] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // inquiry_id currently in confirm state
  const [logAttemptFor, setLogAttemptFor] = useState(null); // inquiry currently in the "log attempt" dialog
  // ── Duplicate flagging (iter 276) ─────────────────────────────────
  // On page load fetch every inquiry-dup group and build a Set of ids
  // that appear in ANY group. Rows in the set render a "Duplicate?" chip
  // that opens a resolution modal.
  const [dupIds, setDupIds] = useState(new Set());
  const [dupModalFor, setDupModalFor] = useState(null);
  const [earlierFor, setEarlierFor] = useState(null);
  useEffect(() => {
    if (getOrg() === 'ludo') return;
    api.get('/duplicates/audit?kind=inquiries')
      .then((r) => {
        const s = new Set();
        for (const g of (r.data?.groups?.inquiries || [])) {
          for (const rec of (g.records || [])) if (rec.inquiry_id) s.add(rec.inquiry_id);
        }
        setDupIds(s);
      })
      .catch(() => setDupIds(new Set()));
  }, [items]);

  const load = async () => {
    const q = new URLSearchParams();
    // 'followup' is a pseudo-filter that pulls new + contacted rows in one
    // call so admins see everything that still needs a touch by default.
    if (filter === 'followup') {
      q.set('disposition', 'new,contacted');
    } else if (filter !== 'all') {
      q.set('disposition', filter);
    }
    if (source !== 'all') q.set('source', source);
    const r = await api.get(`/inquiries?${q.toString()}`);
    setItems(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [filter, source]);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post('/inquiries/sync-google');
      toast.success(`Added ${r.data.added}, updated ${r.data.updated}, skipped ${r.data.skipped}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed — check INQUIRY_SHEET_CSV_URL');
    } finally { setSyncing(false); }
  };

  const disposition = async (id, val) => {
    await api.patch(`/inquiries/${id}`, { disposition: val });
    window.dispatchEvent(new CustomEvent('inquiries:changed'));
    load();
  };

  const convert = async (inq) => {
    // Server-side atomic conversion (iter 38): backend creates the pipeline
    // client + flips the disposition to `converted` in a single call. If any
    // step fails on the server, the inquiry stays as-is. After success we
    // redirect to /pipeline?open=<client_id> — Pipeline reads that param and
    // pops the ClientFormDialog immediately so the admin lands directly in
    // the new profile to fill in the rest of the details.
    try {
      const r = await api.post(`/inquiries/${inq.inquiry_id}/convert`, {});
      window.dispatchEvent(new CustomEvent('inquiries:changed'));
      toast.success('Client created — opening profile');
      window.location.href = `/pipeline?open=${r.data.client_id}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Convert failed');
      load();
    }
  };

  const softDelete = async (inq) => {
    try {
      await api.delete(`/inquiries/${inq.inquiry_id}`);
      window.dispatchEvent(new CustomEvent('inquiries:changed'));
      toast.success('Inquiry deleted');
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="inquiries-page">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#685B72]/70 font-semibold">Follow-up queue</div>
          <h1 className="font-serif text-4xl text-[#2D2833] mt-1">Inquiries</h1>
          <p className="text-[#685B72] mt-1">Website form submissions + fax referrals waiting for admin follow-up.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={sync} disabled={syncing} data-testid="inquiries-sync-btn">
            <RefreshCw size={14} className={syncing ? 'animate-spin mr-2' : 'mr-2'} />
            {syncing ? 'Syncing…' : 'Sync Google Sheet'}
          </Button>
          <FaxDialog open={openFax} onOpenChange={setOpenFax} onSaved={load} />
        </div>
      </div>

      <Card className="rounded-2xl border-[#E8E5EC] p-4 flex flex-wrap gap-3 items-center" data-testid="inquiries-filters">
        <span className="text-xs text-[#685B72] uppercase tracking-widest font-semibold">Show:</span>
        {['followup','new','contacted','no_response','duplicate','not_eligible','not_interested','converted','all'].map(k => (
          <button key={k} onClick={() => setFilter(k)}
            className={`px-3 py-1 rounded-full text-xs border ${filter === k ? 'bg-[#2D2833] text-white border-[#2D2833]' : 'bg-white text-[#685B72] border-[#E8E5EC]'}`}
            data-testid={`filter-${k}`}
          >
            {k === 'all' ? 'All' : k === 'followup' ? 'Needs Follow-up' : DISP_LABEL[k]}
          </button>
        ))}
        <span className="mx-2 h-4 w-px bg-[#E8E5EC]" />
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="h-8 w-[160px] rounded-lg"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="google_form">Website form</SelectItem>
            <SelectItem value="fax">Fax referral</SelectItem>
          </SelectContent>
        </Select>
      </Card>

      <Card className="rounded-2xl border-[#E8E5EC] overflow-hidden" data-testid="inquiries-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#FAF8FB] text-[#685B72] text-left">
              <th className="px-3 py-2 font-semibold">Received</th>
              <th className="px-3 py-2 font-semibold">Client</th>
              <th className="px-3 py-2 font-semibold">Form Submitter</th>
              <th className="px-3 py-2 font-semibold">Clinic</th>
              <th className="px-3 py-2 font-semibold">Source</th>
              <th className="px-3 py-2 font-semibold">Referral source</th>
              <th className="px-3 py-2 font-semibold">Phone</th>
              <th className="px-3 py-2 font-semibold">
                <span className="inline-flex items-center gap-1">
                  Status
                  <HelpTip title="Disposition" testid="help-disposition">
                    Track where each lead is in your follow-up:
                    <ul className="list-disc pl-4 mt-1.5 space-y-0.5">
                      <li><b>New</b> — untouched, sitting in the queue</li>
                      <li><b>Contacted</b> — you reached out (up to 3 attempts logged)</li>
                      <li><b>No Response</b> — attempts made but family never replied</li>
                      <li><b>Duplicate</b> — same family, already in pipeline</li>
                      <li><b>Converted</b> — became a client (auto-set when you click Convert)</li>
                    </ul>
                  </HelpTip>
                </span>
              </th>
              <th className="px-3 py-2 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(i => {
              const attempts = i.contact_attempts || [];
              const canLog = attempts.length < MAX_CONTACT_ATTEMPTS && i.disposition !== 'converted';
              const staleInfo = computeStale(i);
              const rowCls = staleInfo.stale
                ? 'border-t border-[#8A3A3A]/40 bg-[#F5EAEA]'
                : 'border-t border-[#E8E5EC]';
              const telUri = buildTelUri(i.phone);
              return (
              <tr key={i.inquiry_id} className={rowCls} data-testid={`inq-row-${i.inquiry_id}`} data-stale={staleInfo.stale ? 'yes' : 'no'}>
                <td className="px-3 py-2 text-[#685B72] align-top">{fmtDate(i.received_at)}</td>
                <td className="px-3 py-2 text-[#2D2833] font-medium align-top" data-testid={`inq-client-${i.inquiry_id}`}>
                  <div className="flex items-center gap-1.5">
                    {i.client_name}
                    {dupIds.has(i.inquiry_id) && i.disposition !== 'duplicate' && !i.returning_family && (
                      <button
                        type="button"
                        onClick={() => setDupModalFor(i)}
                        className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#B08A3A]/15 text-[#B08A3A] border border-[#B08A3A]/40 hover:bg-[#B08A3A]/25 transition"
                        title="This inquiry has a possible duplicate — click to review and decide"
                        data-testid={`dup-flag-${i.inquiry_id}`}
                      >
                        <Copy size={9} /> Duplicate?
                      </button>
                    )}
                    {i.returning_family && (
                      <button
                        type="button"
                        onClick={() => setEarlierFor(i)}
                        className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#839E97]/15 text-[#5E7A73] border border-[#839E97]/40 hover:bg-[#839E97]/30 transition-colors"
                        title={`This family submitted before${i.returning_prev_disposition ? ` (earlier inquiry: ${DISP_LABEL[i.returning_prev_disposition] || i.returning_prev_disposition})` : ''} — click to open`}
                        data-testid={`returning-badge-${i.inquiry_id}`}
                      >
                        <History size={9} /> Returning family
                      </button>
                    )}
                    {i.disposition === 'duplicate' && i.duplicate_of && (
                      <button
                        type="button"
                        onClick={() => setEarlierFor(i)}
                        className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#685B72]/10 text-[#685B72] border border-[#685B72]/30 hover:bg-[#685B72]/20 transition-colors"
                        title="Family submitted the form again while their earlier inquiry was still open — click to open"
                        data-testid={`repeat-dup-badge-${i.inquiry_id}`}
                      >
                        <Copy size={9} /> Repeat form
                      </button>
                    )}
                    {staleInfo.stale && (
                      <span
                        className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#8A3A3A]/15 text-[#8A3A3A] border border-[#8A3A3A]/40"
                        title={`Contacted ${staleInfo.bd} business day${staleInfo.bd === 1 ? '' : 's'} ago — follow up now`}
                        data-testid={`stale-badge-${i.inquiry_id}`}
                      >
                        <AlertCircle size={9} /> Follow up · {staleInfo.bd}bd
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-[#685B72] align-top" data-testid={`inq-submitter-${i.inquiry_id}`}>
                  {i.form_submitter ? <span>{i.form_submitter}</span> : <span className="text-[#685B72] italic">—</span>}
                </td>
                <td className="px-3 py-2 text-[#685B72] align-top">{i.clinic || '—'}</td>
                <td className="px-3 py-2 align-top">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${i.source === 'fax' ? 'bg-[#BAA9CA]/15 text-[#685B72] border-[#BAA9CA]/30' : 'bg-[#839E97]/15 text-[#839E97] border-[#839E97]/30'}`}>
                    {i.source === 'fax' ? 'Fax' : i.source === 'referral_form' ? 'Referral link' : i.source === 'google_form' ? 'Google form' : 'Website'}
                  </span>
                </td>
                <td className="px-3 py-2 text-[#685B72] align-top">
                  <div>{i.referral_source || '—'}</div>
                  {i.referral_facility_name && (
                    <div className="text-[11px] text-[#685B72] mt-0.5" data-testid={`facility-${i.inquiry_id}`}>
                      {i.referral_facility_name}
                      {i.referral_facility_address && <span className="text-[#685B72]/80"> · {i.referral_facility_address}</span>}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-[#685B72] align-top">
                  {telUri ? (
                    <a
                      href={telUri}
                      className="inline-flex items-center gap-1 text-[#839E97] hover:text-[#839E97] hover:underline font-medium"
                      title="Click to dial via 8x8 Work (or your default tel: handler)"
                      data-testid={`inq-phone-${i.inquiry_id}`}
                    >
                      <PhoneCall size={11} />
                      {formatPhoneDisplay(i.phone) || i.phone}
                    </a>
                  ) : (
                    <span className="text-[#685B72]">—</span>
                  )}
                </td>
                <td className="px-3 py-2 align-top">
                  <Select value={i.disposition} onValueChange={v => disposition(i.inquiry_id, v)}>
                    <SelectTrigger className={`h-7 rounded-full text-[11px] px-2 w-[140px] border ${DISP_TONE[i.disposition] || ''}`} data-testid={`disp-${i.inquiry_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Iter 44: Converted is now selectable manually as
                          well (still auto-set by the Convert button). */}
                      {Object.entries(DISP_LABEL)
                        .map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {attempts.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1" data-testid={`attempts-${i.inquiry_id}`}>
                      {attempts.map((a, idx) => {
                        const parts = [];
                        if (a.method) parts.push(a.method);
                        if (a.outcome) parts.push(a.outcome);
                        const chip = parts.join(' · ') || 'Logged';
                        return (
                          <span
                            key={idx}
                            title={`${fmtDate(a.at)}${a.by ? ` by ${a.by}` : ''}${a.method ? ` · ${a.method}` : ''}${a.outcome ? ` · ${a.outcome}` : ''}${a.note ? ` · "${a.note}"` : ''}`}
                            className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F1EDF5] text-[#685B72] border border-[#E8E5EC]"
                          >
                            #{idx + 1} · {chip}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-right align-top">
                  <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                    {canLog && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setLogAttemptFor(i)}
                        className="h-7 text-[11px]"
                        data-testid={`log-attempt-${i.inquiry_id}`}
                        title={`Log contact attempt (${attempts.length}/${MAX_CONTACT_ATTEMPTS})`}
                      >
                        <PhoneCall size={11} className="mr-1" />
                        Log ({attempts.length}/{MAX_CONTACT_ATTEMPTS})
                      </Button>
                    )}
                    {i.disposition !== 'converted' && (
                      <Button size="sm" variant="outline" onClick={() => convert(i)} data-testid={`convert-${i.inquiry_id}`} className="h-7 text-[11px]">
                        Convert <ArrowRight size={11} className="ml-1" />
                      </Button>
                    )}
                    {canDelete && (
                      confirmDelete === i.inquiry_id ? (
                        <span className="inline-flex items-center gap-1" data-testid={`confirm-row-${i.inquiry_id}`}>
                          <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(null)} className="h-7 px-2 text-[11px]">Cancel</Button>
                          <Button size="sm" onClick={() => softDelete(i)} className="h-7 px-2 text-[11px] bg-[#8A3A3A] hover:bg-[#8A3A3A] text-white" data-testid={`confirm-delete-${i.inquiry_id}`}>
                            Delete
                          </Button>
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setConfirmDelete(i.inquiry_id)}
                          className="h-7 w-7 p-0 text-[#8A3A3A] hover:bg-[#8A3A3A]/10"
                          data-testid={`delete-${i.inquiry_id}`}
                          title="Delete inquiry"
                        >
                          <Trash2 size={13} />
                        </Button>
                      )
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-[#685B72]">
                <Inbox size={20} className="mx-auto mb-2 opacity-50" />
                No inquiries match your filters.
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <ContactAttemptDialog
        inquiry={logAttemptFor}
        onClose={() => setLogAttemptFor(null)}
        onLogged={() => { setLogAttemptFor(null); load(); }}
      />
      <DuplicateResolveDialog
        inquiry={dupModalFor}
        onClose={() => setDupModalFor(null)}
        onResolved={() => { setDupModalFor(null); load(); }}
      />
      <EarlierInquiriesSheet inquiry={earlierFor} onClose={() => setEarlierFor(null)}
        label={(d) => DISP_LABEL[d] || d || '—'} />
    </div>
  );
}


// ── Duplicate resolution modal (iter 276) ────────────────────────────
// When an admin clicks the "Duplicate?" flag on an inquiry row we open
// this dialog. It fetches /api/inquiries/{id}/duplicates to show the
// matched records (other inquiries + existing clients) and offers three
// clean actions: (a) mark THIS inquiry as duplicate, (b) mark the OTHER
// inquiry as duplicate, (c) keep both — mark as reviewed. All actions
// are single-inquiry patches so nothing is destructive.
function DuplicateResolveDialog({ inquiry, onClose, onResolved }) {
  const [matches, setMatches] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!inquiry) return;
    setMatches(null);
    api.get(`/inquiries/${inquiry.inquiry_id}/duplicates`)
      .then((r) => setMatches(r.data?.matches || []))
      .catch(() => setMatches([]));
  }, [inquiry]);
  if (!inquiry) return null;
  const markDup = async (id) => {
    setSaving(true);
    try {
      await api.patch(`/inquiries/${id}`, { disposition: 'duplicate' });
      toast.success('Marked as duplicate');
      window.dispatchEvent(new CustomEvent('inquiries:changed'));
      onResolved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="dup-resolve-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl flex items-center gap-2 text-[#B08A3A]">
            <Copy size={18} /> Resolve duplicate — {inquiry.client_name}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#685B72] leading-relaxed">
          We found possible matches for this inquiry. Choose which record is the true duplicate — the other will remain active for follow-up.
        </p>
        <div className="rounded-lg border border-[#E9E3DA] divide-y max-h-72 overflow-auto" data-testid="dup-resolve-list">
          {/* This inquiry itself, so admin can pick "mark THIS one" */}
          <div className="p-3 flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-[#2D2833]">{inquiry.client_name} <span className="text-[10px] uppercase tracking-widest text-[#B08A3A] ml-1">This inquiry</span></div>
              <div className="text-[11px] text-[#685B72] mt-0.5">
                Received {fmtDate(inquiry.received_iso || inquiry.received_at)} · {inquiry.clinic || '—'} · {inquiry.phone || 'no phone'}
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              disabled={saving}
              onClick={() => markDup(inquiry.inquiry_id)}
              className="h-7 text-[11px]"
              data-testid="dup-mark-this"
            >
              Mark this as duplicate
            </Button>
          </div>
          {matches === null && (
            <div className="p-6 text-center text-sm text-[#685B72]">Loading matches…</div>
          )}
          {matches !== null && matches.length === 0 && (
            <div className="p-6 text-center text-sm text-[#685B72] italic">No live matches remaining — this may have already been resolved.</div>
          )}
          {(matches || []).map((m, i) => (
            <div key={i} className="p-3 flex items-start justify-between gap-3" data-testid={`dup-resolve-row-${i}`}>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-[#2D2833]">
                  {m.client_name}
                  <span className="text-[9px] uppercase tracking-widest ml-2 px-1.5 py-0.5 rounded-full" style={{ background: m.kind === 'client' ? '#839E9722' : '#B08A3A22', color: m.kind === 'client' ? '#839E97' : '#B08A3A' }}>
                    {m.kind === 'client' ? 'Existing client' : 'Inquiry'}
                  </span>
                </div>
                <div className="text-[11px] text-[#685B72] mt-0.5">
                  {m.kind === 'client'
                    ? <>{m.clinic || '—'} · {m.stage || '—'}{m.phone ? ` · ${m.phone}` : ''}{m.dob ? ` · DOB ${m.dob}` : ''}</>
                    : <>Received {fmtDate(m.received_iso)} · {m.clinic || '—'} · {m.disposition || 'new'}{m.phone ? ` · ${m.phone}` : ''}</>}
                  <span className="ml-1 text-[10px] italic">matched on {m.reason?.replace('_', '+') || 'name'}</span>
                </div>
              </div>
              {m.kind === 'inquiry' ? (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={saving}
                  onClick={() => markDup(m.inquiry_id)}
                  className="h-7 text-[11px]"
                  data-testid={`dup-mark-other-${i}`}
                >
                  Mark this one as duplicate
                </Button>
              ) : (
                <a
                  href={`/pipeline?open=${m.client_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[11px] font-semibold text-[#B08A3A] hover:underline"
                  data-testid={`dup-open-client-${i}`}
                >
                  Open client →
                </a>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-1">
          <Button variant="ghost" onClick={onClose} data-testid="dup-resolve-close">Cancel</Button>
          <Button
            variant="outline"
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              try {
                await api.patch(`/inquiries/${inquiry.inquiry_id}`, { disposition_notes: 'Reviewed — not a duplicate' });
                toast.success('Kept both — marked as reviewed');
                onResolved();
              } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
              finally { setSaving(false); }
            }}
            data-testid="dup-keep-both"
          >
            Keep both — not a duplicate
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ContactAttemptDialog({ inquiry, onClose, onLogged }) {
  const [method, setMethod] = useState('Text');
  const [outcome, setOutcome] = useState('Reached');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!inquiry) { setMethod('Text'); setOutcome('Reached'); setNote(''); }
  }, [inquiry]);
  if (!inquiry) return null;
  const attempts = inquiry.contact_attempts || [];
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/inquiries/${inquiry.inquiry_id}/contact-attempt`, { method, outcome, note });
      window.dispatchEvent(new CustomEvent('inquiries:changed'));
      toast.success(`Attempt ${attempts.length + 1} logged (${method} · ${outcome})`);
      onLogged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="attempt-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Log contact attempt for {inquiry.client_name}</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-[#685B72] -mt-2">Attempt {attempts.length + 1} of {MAX_CONTACT_ATTEMPTS}. After 3 unsuccessful attempts, flip status to No Response or Duplicate.</div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="text-xs text-[#685B72]">Method</label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="rounded-xl mt-1 h-9" data-testid="attempt-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['Text', 'Email', 'Call', 'Voicemail', 'In-person', 'Other'].map(m => (
                  <SelectItem key={m} value={m} data-testid={`attempt-method-opt-${m.replace(/\W+/g, '_')}`}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-[#685B72]">Outcome</label>
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger className="rounded-xl mt-1 h-9" data-testid="attempt-outcome"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['Reached', 'Left Voicemail', 'No Answer', 'Wrong Number', 'Callback Requested', 'Not Interested', 'Other'].map(o => (
                  <SelectItem key={o} value={o} data-testid={`attempt-outcome-opt-${o.replace(/\W+/g, '_')}`}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-3">
          <label className="text-xs text-[#685B72]">Note (optional)</label>
          <Textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="e.g. Mom will call back Tuesday after 3pm"
            className="rounded-xl mt-1"
            rows={3}
            data-testid="attempt-note"
            maxLength={500}
          />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#685B72] hover:bg-[#52475A] text-white" data-testid="attempt-save">
            {saving ? 'Saving…' : 'Log attempt'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY_FAX_FORM = () => ({
  received_at: new Date().toISOString().slice(0, 10),
  client_name: '',
  phone: '',
  insurance: '',
  clinic: '',
  referral_source: '',
  referral_facility_name: '',
  referral_facility_address: '',
  notes: '',
});

function FaxDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(EMPTY_FAX_FORM);
  const [clinics, setClinics] = useState([]);
  useEffect(() => { if (open) {
    api.get('/clinics').then(r => setClinics(r.data.map(c => c.name)));
  }}, [open]);

  const showFacility = form.referral_source && form.referral_source !== 'Others';

  // Client-side required-field guard (backend re-checks). Every red-asterisk
  // field must be non-empty before Save is enabled.
  const required = ['client_name', 'received_at', 'clinic', 'phone'];
  const missing = required.filter(k => !(form[k] || '').trim());

  const save = async () => {
    if (missing.length > 0) {
      toast.error(`Required: ${missing.map(k => ({client_name:'Client Name',received_at:'Date Received',clinic:'Clinic',phone:'Phone'}[k])).join(', ')}`);
      return;
    }
    try {
      const payload = { ...form };
      // Facility fields are only sent when a specific source is selected.
      if (!showFacility) {
        payload.referral_facility_name = '';
        payload.referral_facility_address = '';
      }
      await api.post('/inquiries/fax', payload);
      toast.success('Fax referral logged');
      onOpenChange(false);
      onSaved();
      setForm(EMPTY_FAX_FORM());
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="log-fax-btn"><Printer size={14} className="mr-2" /> Log fax referral</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="fax-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Log fax referral</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="col-span-1">
            <label className="text-xs text-[#685B72]">Date received <span className="text-[#8A3A3A]">*</span></label>
            <Input type="date" value={form.received_at} onChange={e => setField('received_at', e.target.value)} data-testid="fax-date" />
          </div>
          <div className="col-span-1">
            <label className="text-xs text-[#685B72]">Clinic <span className="text-[#8A3A3A]">*</span></label>
            <Select value={form.clinic} onValueChange={v => setField('clinic', v)}>
              <SelectTrigger data-testid="fax-clinic"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>{clinics.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <label className="text-xs text-[#685B72]">Client name <span className="text-[#8A3A3A]">*</span></label>
            <Input value={form.client_name} onChange={e => setField('client_name', e.target.value)} data-testid="fax-name" />
          </div>
          <div className="col-span-1">
            <label className="text-xs text-[#685B72]">Phone <span className="text-[#8A3A3A]">*</span></label>
            <Input value={form.phone} onChange={e => setField('phone', e.target.value)} data-testid="fax-phone" />
          </div>
          <div className="col-span-1">
            <label className="text-xs text-[#685B72]">Insurance</label>
            <Input value={form.insurance} onChange={e => setField('insurance', e.target.value)} data-testid="fax-insurance" />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-[#685B72]">Referral source</label>
            <Select value={form.referral_source} onValueChange={v => setField('referral_source', v === '__none__' ? '' : v)}>
              <SelectTrigger data-testid="fax-source"><SelectValue placeholder="Select referral source" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__" className="italic text-[#685B72]" data-testid="fax-source-none">— None —</SelectItem>
                {FAX_REFERRAL_SOURCES.map(s => <SelectItem key={s} value={s} data-testid={`fax-source-opt-${s.replace(/\W+/g,'_')}`}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {/* Facility name + address surface for every source except "Others"
              (iter 38). Manual entry — Maps auto-complete will be added later. */}
          {showFacility && (
            <>
              <div className="col-span-2">
                <label className="text-xs text-[#685B72]">Facility name</label>
                <Input
                  value={form.referral_facility_name}
                  onChange={e => setField('referral_facility_name', e.target.value)}
                  placeholder={`e.g. ${form.referral_source === 'Pediatrician' ? 'Dr. Rodriguez Pediatrics' : form.referral_source + ' name'}`}
                  data-testid="fax-facility-name"
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-[#685B72]">Facility address</label>
                <Input
                  value={form.referral_facility_address}
                  onChange={e => setField('referral_facility_address', e.target.value)}
                  placeholder="Street, City, State ZIP"
                  data-testid="fax-facility-address"
                />
              </div>
            </>
          )}
          <div className="col-span-2">
            <label className="text-xs text-[#685B72]">Notes</label>
            <Textarea rows={3} value={form.notes} onChange={e => setField('notes', e.target.value)} data-testid="fax-notes" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={save}
            disabled={missing.length > 0}
            data-testid="fax-save"
            className="bg-[#685B72] hover:bg-[#52475A] text-white disabled:bg-[#D8D6E2] disabled:text-[#685B72]"
          >
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
